import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useFlag } from '@innovamat/flags';
import { useCurrentNavigationValues, useUser } from '@innovamat/ga-features';
import { EmptyReport } from '@innovamat/glimmer-assets';
import type { TabType } from '@innovamat/glimmer-components';
import {
  AnnouncementPanel,
  Button,
  EmptyState,
  Icon,
  InnerHtml,
  snack,
  Tabs,
  Typography,
} from '@innovamat/glimmer-components';
import { downloadBase64PdfFile } from '@innovamat/radiance-utils';

import { MockExamsDistributionChart } from '../../../../../components/mock-exams-distribution-chart';
import { mockExamsStudentRowsToChartData } from '../../../../../modules/mock-exams/mock-exams';
import type { MockExamsStudentRow } from '../../../../../types/mock-exams';
import { InfoTag } from '../components/info-tag';
import { useTestAndResults } from '../context/test-and-results-provider';
import { DownloadTestQuestionsModal } from './components/download-test-questions-modal/download-test-questions-modal';
import { MockExamsDrawerStatementsTable } from './components/mock-exams-drawer-statements-table';
import { MockExamsDrawerTable } from './components/mock-exams-drawer-table';
import { useGetTestQuestionsAvailableLanguages } from './hooks/use-get-test-questions-available-languages';
import { useGetTestQuestionsPdf } from './hooks/use-get-test-questions-pdf';

const TABS_ID = {
  STUDENTS: 'students',
  DISTRIBUTION: 'distribution',
  QUESTIONS: 'questions',
};

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const FlexContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 600px;
`;

const InfoTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Description = styled(Typography.Body2)`
  margin-top: 8px;
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

const DownloadLink = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  path {
    fill: ${({ theme }) =>
      theme.tokens.color.alias.cm.icon['icon-accent'].value};
  }
`;

const TabsContent = styled.div``;

const TabsContainer = styled.div`
  margin-bottom: 16px;
  border-bottom: 1px solid
    ${({ theme }) => theme.tokens.color.alias.cm.border['border-subtle'].value};
`;

const StyledAnnouncementPanel = styled(AnnouncementPanel)`
  margin-bottom: 16px;
  margin-left: auto;
  width: fit-content;
`;

type MockExamsDrawerContentProps = {
  detailData: any;
  date: string;
  participation: string;
};

export function MockExamsDrawerContent({
  detailData,
  date,
  participation,
}: MockExamsDrawerContentProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const { rowSelected } = useTestAndResults();
  const { currentClassroom } = useCurrentNavigationValues();
  const { user } = useUser();

  const [showDownloadTestQuestionsModal, setShowDownloadTestQuestionsModal] =
    useState(false);
  const [testQuestionsAvailableLangs, setTestQuestionsAvailableLangs] =
    useState<string[] | []>([]);
  const [testQuestionsLanguage, setTestQuestionsLanguage] = useState<
    string | undefined
  >();

  const language = testQuestionsLanguage ?? user?.locale ?? i18n.language;

  const { value: printMockExam } = useFlag('printMockExams', false, {
    identifier: '',
    custom: {
      schoolId: user?.organizationId || '',
    },
    email: user?.email || '',
  });

  const { value: useMockData } = useFlag('schoolDemoMockReports', false, {
    identifier: '',
    custom: {
      schoolId: user?.organizationId || '',
      classroomId: currentClassroom?.id || '',
    },
  });

  const { refetch: refetchAvailableLanguages } =
    useGetTestQuestionsAvailableLanguages({
      testId: rowSelected?.testId ?? '',
      enabled: false,
    });

  const { refetch: refetchQuestionsPdf, isLoading: isDownloadingPdf } =
    useGetTestQuestionsPdf({
      testId: rowSelected?.testId ?? '',
      language: language,
      enabled: false,
    });

  const chartData = useMemo(() => {
    return mockExamsStudentRowsToChartData(
      detailData?.students as MockExamsStudentRow[],
      t
    );
  }, [detailData, t]);

  const [selectedTab, setSelectedTab] = useState(TABS_ID.STUDENTS);

  const MOCK_EXAMS_TABS: TabType[] = [
    {
      dataInlineManual: `mock-exams-tab-${TABS_ID.STUDENTS}`,
      selected: selectedTab === TABS_ID.STUDENTS,
      text: t(`mock-exams-drawer.tabs.${TABS_ID.STUDENTS}`),
      onClick: () => setSelectedTab(TABS_ID.STUDENTS),
    },
    {
      dataInlineManual: `mock-exams-tab-${TABS_ID.DISTRIBUTION}`,
      selected: selectedTab === TABS_ID.DISTRIBUTION,
      text: t(`mock-exams-drawer.tabs.${TABS_ID.DISTRIBUTION}`),
      onClick: () => setSelectedTab(TABS_ID.DISTRIBUTION),
    },
    {
      dataInlineManual: `mock-exams-tab-${TABS_ID.QUESTIONS}`,
      selected: selectedTab === TABS_ID.QUESTIONS,
      text: t(`mock-exams-drawer.tabs.${TABS_ID.QUESTIONS}`),
      onClick: () => setSelectedTab(TABS_ID.QUESTIONS),
    },
  ];

  const downloadTestQuestionsPdf = async (newWindow = false): Promise<void> => {
    const currentYear = new Date().getFullYear();
    const fileName = `${t('reports.title')}_${t(
      rowSelected?.rowName ?? ''
    )}_${currentYear}.pdf`;

    if (useMockData) {
      try {
        const module = await import(
          `./fake-questions-data/MOCKEXM-questions-en`
        );
        const pdfBase64 = module.default;
        if (typeof downloadBase64PdfFile === 'function') {
          await downloadBase64PdfFile({
            base64Content: pdfBase64,
            fileName,
            openInNewTab: newWindow,
          });
        }
      } catch (error) {
        console.error(`Error on download`, error);
      }
      return;
    }

    const testQuestionsPdfResponse = await refetchQuestionsPdf();

    if (testQuestionsPdfResponse.isError) {
      snack.error(t('mock-exams-drawer.error.downloading-statements-pdf'));
      return;
    }

    const windowRef = newWindow
      ? window.open('about:blank', '_blank')
      : undefined;

    await downloadBase64PdfFile({
      base64Content:
        testQuestionsPdfResponse?.data?.testQuestionsPdf?.pdfBase64 ?? '',
      fileName,
      openInNewTab: newWindow,
      windowRef: windowRef ?? undefined,
    })
      .catch(() => {
        snack.error(t('mock-exams-drawer.error.downloading-statements-pdf'));
      })
      .finally(() => {
        setTestQuestionsLanguage(undefined);
        setShowDownloadTestQuestionsModal(false);
      });
  };

  const handleCloseTestQuestionsModal = (): void => {
    setShowDownloadTestQuestionsModal(false);
    setTestQuestionsAvailableLangs([]);
  };

  const handleTestQuestionsDownload = async (
    language: string
  ): Promise<void> => {
    setTestQuestionsLanguage(language);
  };

  const handleSeeQuestionsClick = async (): Promise<void> => {
    if (useMockData) {
      downloadTestQuestionsPdf(true);
      return;
    }

    const availableLanguagesResponse = await refetchAvailableLanguages();
    const availableLanguages =
      availableLanguagesResponse.data?.testQuestionsAvailableLanguages || [];

    if (availableLanguages.length === 0) {
      snack.error(t('mock-exams-drawer.error.no-statements-available'));
      return;
    }

    const showModalWhenUserLangNotAvailable =
      !availableLanguages.includes(language);

    if (showModalWhenUserLangNotAvailable) {
      setTestQuestionsAvailableLangs(availableLanguages);
      setShowDownloadTestQuestionsModal(true);
      return;
    }

    downloadTestQuestionsPdf(true);
  };

  const handlePrintMockExam = () => {
    if (!printMockExam) return;

    const classroomName = encodeURIComponent(currentClassroom?.name ?? '');
    const organizationName = encodeURIComponent(user?.organizationName ?? '');

    window.open(
      `/classroom/${currentClassroom?.id}/print-mock-exams/${rowSelected?.id}/${classroomName}/${organizationName}`,
      '_blank'
    );
  };

  useEffect(() => {
    if (detailData?.students?.length === 0) setSelectedTab(TABS_ID.STUDENTS);
  }, [detailData]);

  useEffect(() => {
    if (testQuestionsLanguage && showDownloadTestQuestionsModal) {
      downloadTestQuestionsPdf(false);
    }
  }, [testQuestionsLanguage]);

  const isPrintDisabled = !printMockExam || detailData?.students.length === 0;

  return (
    <>
      <ContentContainer>
        <FlexContainer>
          <InfoContainer>
            {rowSelected?.rowDescription && (
              <Description>
                <InnerHtml
                  text={t(
                    rowSelected?.rowDescription || 'mock-exams-generic-desc'
                  )}
                />
              </Description>
            )}
            <InfoTagsContainer>
              <InfoTag text={date} />
              <InfoTag
                title={t('mock-exams-drawer.info-tag.participation')}
                text={participation}
              />
              <DownloadLink onClick={handleSeeQuestionsClick}>
                <Typography.Link2>
                  {t('mock-exams-drawer.link.see-statements')}
                </Typography.Link2>
                <Icon icon="OpenNewTabIcon" size="S" />
              </DownloadLink>
            </InfoTagsContainer>
          </InfoContainer>
          <Button
            variant="secondary"
            leftIcon="DownloadIcon"
            oneLine
            disabled={isPrintDisabled}
            onClick={handlePrintMockExam}
          >
            {t('mock-exams-drawer.generate-button')}
          </Button>
        </FlexContainer>
        <TabsContent>
          {detailData?.students?.length === 0 ? (
            <EmptyState
              Image={EmptyReport}
              title={t('mock-exams-drawer.emptyState.title')}
              subtitle={t('mock-exams-drawer.emptyState.subtitle')}
            />
          ) : (
            <>
              <TabsContainer>
                <Tabs tabs={MOCK_EXAMS_TABS} />
              </TabsContainer>

              {selectedTab === TABS_ID.STUDENTS && (
                <MockExamsDrawerTable
                  students={
                    (detailData?.students as MockExamsStudentRow[]) ?? []
                  }
                  numStatements={detailData?.numberOfStatements ?? 0}
                  statementsMetadata={detailData?.statementsMeta}
                />
              )}

              {selectedTab === TABS_ID.DISTRIBUTION && (
                <MockExamsDistributionChart data={chartData} />
              )}

              {selectedTab === TABS_ID.QUESTIONS && (
                <>
                  {detailData?.statements?.evaluations?.length === 0 ? (
                    <EmptyState
                      Image={EmptyReport}
                      title={t(
                        'mock-exams-drawer.statements-table.emptyState.title'
                      )}
                      subtitle={t(
                        'mock-exams-drawer.statements-table.emptyState.subtitle'
                      )}
                    />
                  ) : (
                    <>
                      <StyledAnnouncementPanel
                        type="warning"
                        text={
                          <Trans
                            i18nKey="mock-exams-drawer.statements-table.announcement"
                            components={{
                              b: <b />,
                            }}
                          />
                        }
                        canClose={false}
                      />
                      <MockExamsDrawerStatementsTable
                        statements={detailData?.statements?.evaluations ?? []}
                        multipleChoiceLabels={
                          detailData?.statements
                            ?.multipleChoicePositionLabels ?? []
                        }
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}
        </TabsContent>
      </ContentContainer>
      {testQuestionsAvailableLangs.length > 0 && (
        <DownloadTestQuestionsModal
          isOpen={showDownloadTestQuestionsModal}
          isDownloading={isDownloadingPdf}
          languages={testQuestionsAvailableLangs}
          onClose={handleCloseTestQuestionsModal}
          onDownload={handleTestQuestionsDownload}
        />
      )}
    </>
  );
}

export {
  ContentContainer,
  Description,
  FlexContainer,
  InfoContainer,
  InfoTagsContainer,
};
